// @flow
/** @jsx jsx */

import React, {useEffect, useRef, useState} from 'react';
import {jsx} from '@core-ui/react-stitch';
import {CategoryNav} from '@core-ui/category-nav';
import {Transition, transitions} from '@core-ui/motion';
import {useLocalize} from '@core-ui/localization-provider';
import {adaptNavigationTrackingValue} from './collaborators/adapt-navigation-tracking-value';
import ToggleButton from './ToggleButton';
import Chevron from './Chevron';
import {COLLAPSED_TOGGLE_BUTTON, EXPANDED_TOGGLE_BUTTON} from './translations';

type CategoryNavigationProps = {
  categoryNavData: SectionData[],
  onToggle: (expanded: boolean) => void,
  isExpanded?: boolean,
}

const transitionTimeout = {
  enter:
    parseFloat(transitions.expressive.in.transitionDuration) +
    parseFloat(transitions.userTriggeredDelay),
  exit:
    parseFloat(transitions.performance.out.transitionDuration) +
    parseFloat(transitions.userTriggeredDelay),
};

const trimSection = (
  section: SectionData,
  length: number,
): SectionData => ({
  ...section,
  children: section.children.slice(0, length),
});

const getSectionLength = (
  section: SectionData,
): number => section?.children?.length ?? 0;

const getShowToggleButton = (allSections, firstSectionLength, secondSectionLength, maxLength) => {
  return allSections.length > 2 || firstSectionLength + secondSectionLength > maxLength;
};

const getSections = (expanded, allSections, collapsedSections) => {
  return  expanded ? allSections : collapsedSections;
};

const getToggleButtonText = (expanded, localize) => {
  return expanded ? localize(EXPANDED_TOGGLE_BUTTON) : localize(COLLAPSED_TOGGLE_BUTTON);
};

const getTransitionCSS = (expanded, transitions) => {
  return expanded
    ? `${transitions.expressive.in.transitionDuration} ${transitions.expressive.in.transitionTimingFunction} ${transitions.userTriggeredDelay}`
    : `${transitions.performance.out.transitionDuration} ${transitions.performance.out.transitionTimingFunction} ${transitions.userTriggeredDelay}`;
};

const getToggleButtonFragment = (showToggleButton, toggleExpanded, toggleButtonText, expanded) => {
  return showToggleButton 
  ? <ToggleButton onClick={toggleExpanded}>
      {toggleButtonText} <Chevron expanded={expanded}/>
    </ToggleButton> 
  : null
};

const getCollapsedFirstSection = (firstSectionLength, maxLength, firstSection) => {
  if (firstSectionLength >= maxLength) {
    return trimSection(firstSection, maxLength);
  } else {
    return {...firstSection};
  }
};

const getCollapsedSecondSection = (firstSectionLength, maxLength, secondSectionLength, secondSection) => {
  if (firstSectionLength >= maxLength) {
    return {};
  } else if (firstSectionLength + secondSectionLength > maxLength) {
    const sectionLength = maxLength - firstSectionLength;
    return trimSection(secondSection, sectionLength);
  } else {
    return {...secondSection};
  }
};

const CategoryNavigation = ({categoryNavData}: CategoryNavigationProps) => {
  const maxLength = 7;
  const allSections = adaptNavigationTrackingValue(categoryNavData);

  let [firstSection, secondSection] = allSections;
  let collapsedFirstSection, collapsedSecondSection;

  const firstSectionLength = getSectionLength(firstSection);
  const secondSectionLength = getSectionLength(secondSection);
  collapsedFirstSection = getCollapsedFirstSection(firstSectionLength, maxLength, firstSection);
  collapsedSecondSection = getCollapsedSecondSection(firstSectionLength, maxLength, secondSectionLength, secondSection);

  const collapsedSections = [
    collapsedFirstSection,
    collapsedSecondSection,
  ].filter((section = {}) => section.children?.length > 0);

  const contentRef = useRef();
  const [transitionStyles, setTransitionStyles] = useState({
    entering: {},
    entered: {},
    exiting: {},
    exited: {},
    unmounted: {},
  });
  const [expanded, setExpanded] = useState(false);
  const {localize} = useLocalize();
  const showToggleButton = getShowToggleButton(allSections, firstSectionLength, secondSectionLength, maxLength);
  const toggleExpanded = () => setExpanded(!expanded);
  const sections = getSections(expanded, allSections, collapsedSections);
  const toggleButtonText = getToggleButtonText(expanded, localize);

  useEffect(() => {
      const contentHeight = contentRef?.current?.offsetHeight ?? 0;
      setTransitionStyles({
        entering: {height: contentHeight, opacity: 1},
        entered: {height: contentHeight, opacity: 1},
        exiting: {height: 0, opacity: 0},
        exited: {height: 0, opacity: 0},
        unmounted: {},
      });
  }, [expanded]);

  const categoryNavigationStyle = theme => ({
    borderBottom: `1px solid ${theme.color.gray20}`,
    paddingTop: '.75rem',
  });

  const transitionCSS = getTransitionCSS(expanded, transitions);

  const contentWrapperStyle = ({transitionState}) => ({
    overflow: 'hidden',
    transition: `height ${transitionCSS}, opacity ${transitionCSS}`,
    ...transitionStyles[transitionState],
  });

  const contentStyle = () => ({
    display: 'block',
    height: 'auto',
    overflow: 'hidden',
  });

  const toggleButtonFragment = getToggleButtonFragment(showToggleButton, toggleExpanded, toggleButtonText, expanded);

  return allSections.length === 0 ? null : (
    <div css={categoryNavigationStyle}>
      <Transition in={true} timeout={transitionTimeout}>
        {transitionState => (
          <div css={() => contentWrapperStyle({transitionState})}>
            <div ref={contentRef} css={contentStyle}>
              <CategoryNav 
                categoryNavData={sections}
                skipLinkHREF="#faceted-grid"
                categoryNavAllData={allSections}
              />
            </div>
          </div>
        )}
      </Transition>
      {toggleButtonFragment}
    </div>
  );
};

export default CategoryNavigation;
